import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IconFileText,
    IconSparkles,
    IconPrinter,
    IconBriefcase,
    IconTag,
    IconCategory,
    IconCalendar,
    IconChevronRight,
    IconTemplate,
    IconWand,
    IconPhoto,
    IconCoin,
    IconUser,
    IconWorld,
    IconExternalLink,
} from '@tabler/icons-react';
import { useAuthStore } from '../../../store/authStore';
import ModuleSquircleIcon from '@/components/common/ModuleSquircleIcon';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import templateSetService from '../../../services/templateSetService';
import aiTemplateService from '../../../services/aiTemplateService';
import jobService from '../../../services/jobService';
import tagService from '../../../services/tagService';
import categoryService from '../../../services/categoryService';
import aiTemplateCategoryService from '../../../services/aiTemplateCategoryService';
import printTemplateService from '../../../services/printTemplateService';

export default function AdminDashboard() {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';

    const fmtNum = (n) => isBn
        ? String(n ?? 0).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[d])
        : (n ?? 0).toLocaleString();

    const dateLabel = new Intl.DateTimeFormat(isBn ? 'bn-BD' : 'en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(new Date());

    const [counts, setCounts] = useState({
        templateSets: 0, aiTemplates: 0, jobs: 0,
        tags: 0, categories: 0, aiCategories: 0,
        printTemplates: 0, drafts: 0,
    });
    const [draftSets, setDraftSets] = useState([]);

    useEffect(() => { loadDashboardData(); }, []);

    const loadDashboardData = async () => {
        const pick = (r) => r?.pagination?.total ?? r?.data?.length ?? 0;
        try {
            const [ts, ai, jobs, tags, cats, aiCats, prints, drafts] = await Promise.all([
                templateSetService.getAllTemplateSets({ page: 1, limit: 1 }).catch(() => null),
                aiTemplateService.list({ page: 1, limit: 1 }).catch(() => null),
                jobService.adminList({ page: 1, limit: 1 }).catch(() => null),
                tagService.getAllTags().catch(() => null),
                categoryService.getAllCategories().catch(() => null),
                aiTemplateCategoryService.list().catch(() => null),
                printTemplateService.list({ page: 1, limit: 1 }).catch(() => null),
                templateSetService.getAllTemplateSets({ page: 1, limit: 6, status: 'draft' }).catch(() => null),
            ]);
            setCounts({
                templateSets: pick(ts),
                aiTemplates: pick(ai),
                jobs: pick(jobs),
                tags: pick(tags),
                categories: pick(cats),
                aiCategories: aiCats?.data?.length ?? pick(aiCats),
                printTemplates: pick(prints),
                drafts: pick(drafts),
            });
            setDraftSets(drafts?.data || []);
        } catch (error) {
            console.error('Failed to load admin dashboard data:', error);
        }
    };

    // Admin's own credit balance (same field as regular users).
    const creditRemaining = user?.credit ?? 0;

    // Primary content tiles (left summary card) — every content type is one click away,
    // filling the card into a full 2×4 quick-nav grid.
    // Single ordered grid of admin quick-access tiles. Types:
    //   count  → shows a total (value + "মোট")           action → shows a "manage" hint
    //   stat   → shows a value + custom sub-label          external → opens a URL in a new tab
    //   blank  → reserved empty cell
    const statTiles = [
        { key: 'templateSets', type: 'count', label: t('navigation.documentSets'),        value: counts.templateSets,   Icon: IconFileText,  path: '/admin/template-sets',  icon: '/module-icons/document-set.svg',
          tile: 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/60',            accent: 'text-blue-600 dark:text-blue-400' },
        { key: 'aiTemplates',  type: 'count', label: t('navigation.aiTemplates'),         value: counts.aiTemplates,    Icon: IconSparkles,  path: '/admin/ai-templates',  icon: '/module-icons/ai-templates.svg',
          tile: 'bg-violet-50 dark:bg-violet-900/20 border-violet-100 dark:border-violet-800/60',    accent: 'text-violet-600 dark:text-violet-400' },
        { key: 'prints',       type: 'count', label: t('navigation.printTemplates'),      value: counts.printTemplates, Icon: IconPrinter,   path: '/admin/print-templates',  icon: '/module-icons/print-media.svg',
          tile: 'bg-teal-50 dark:bg-teal-900/20 border-teal-100 dark:border-teal-800/60',            accent: 'text-teal-600 dark:text-teal-400' },
        { key: 'jobs',         type: 'count', label: t('navigation.adminJobs'),           value: counts.jobs,           Icon: IconBriefcase, path: '/admin/jobs',  icon: '/module-icons/job.svg',
          tile: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/60', accent: 'text-emerald-600 dark:text-emerald-400' },
        { key: 'aiEditor',     type: 'action', label: t('navigation.generateImageAI'),     Icon: IconPhoto,     path: '/admin/generate-image',  icon: '/module-icons/ai-tools.svg',
          tile: 'bg-sky-50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-800/60',                accent: 'text-sky-600 dark:text-sky-400' },
        { key: 'manualEditor', type: 'action', label: t('navigation.generateImageManual'), Icon: IconPhoto,     path: '/admin/generate-image-manual',  icon: '/module-icons/manual-photo-edit.svg',
          tile: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-800/60',    accent: 'text-indigo-600 dark:text-indigo-400' },
        { key: 'tags',         type: 'count', label: t('navigation.tags'),                value: counts.tags,           Icon: IconTag,       path: '/admin/tags',  grad: 'bg-[linear-gradient(145deg,#fbbf24,#d97706)]',
          tile: 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/60',        accent: 'text-amber-600 dark:text-amber-400' },
        { key: 'aiCats',       type: 'count', label: t('navigation.aiTemplateCategories'), value: counts.aiCategories,  Icon: IconWand,      path: '/admin/ai-template-categories',  grad: 'bg-[linear-gradient(145deg,#e879f9,#a21caf)]',
          tile: 'bg-fuchsia-50 dark:bg-fuchsia-900/20 border-fuchsia-100 dark:border-fuchsia-800/60', accent: 'text-fuchsia-600 dark:text-fuchsia-400' },
        { key: 'profile',      type: 'action', label: t('navigation.profile'),             Icon: IconUser,      path: '/admin/profile',  grad: 'bg-[linear-gradient(145deg,#fb7185,#e11d48)]',
          tile: 'bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800/60',            accent: 'text-rose-600 dark:text-rose-400' },
        { key: 'balance',      type: 'stat',  label: t('dashboard.creditBalance'),        value: creditRemaining, sub: t('dashboard.creditsUnit'), Icon: IconCoin, path: '/admin/settings',  grad: 'bg-[linear-gradient(145deg,#34d399,#059669)]',
          tile: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/60', accent: 'text-emerald-600 dark:text-emerald-400' },
        { key: 'website',      type: 'external', label: t('dashboard.sohozkajWebsite'),    Icon: IconWorld,     url: 'https://sohozkaj.com', hint: 'sohozkaj.com',  grad: 'bg-[linear-gradient(145deg,#22d3ee,#0e7490)]',
          tile: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-100 dark:border-cyan-800/60',            accent: 'text-cyan-600 dark:text-cyan-400' },
        { key: 'blank',        type: 'blank' },
    ];

    // Manage shortcuts (Tools & Features) — every admin module, one click away.
    const manageCards = [
        { key: 'ts',      title: t('navigation.documentSets'),         Icon: IconFileText,  path: '/admin/template-sets',  icon: '/module-icons/document-set.svg' },
        { key: 'ai',      title: t('navigation.aiTemplates'),          Icon: IconSparkles,  path: '/admin/ai-templates',  icon: '/module-icons/ai-templates.svg' },
        { key: 'pt',      title: t('navigation.printTemplates'),       Icon: IconPrinter,   path: '/admin/print-templates',  icon: '/module-icons/print-media.svg' },
        { key: 'genAi',   title: t('navigation.generateImageAI'),      Icon: IconPhoto,     path: '/admin/generate-image',  icon: '/module-icons/ai-tools.svg' },
        { key: 'genMan',  title: t('navigation.generateImageManual'),  Icon: IconPhoto,     path: '/admin/generate-image-manual',  icon: '/module-icons/manual-photo-edit.svg' },
        { key: 'aiCats',  title: t('navigation.aiTemplateCategories'), Icon: IconWand,      path: '/admin/ai-template-categories',  grad: 'bg-[linear-gradient(145deg,#e879f9,#a21caf)]' },
        { key: 'jobs',    title: t('navigation.adminJobs'),            Icon: IconBriefcase, path: '/admin/jobs',  icon: '/module-icons/job.svg' },
        { key: 'tags',    title: t('navigation.tags'),                 Icon: IconTag,       path: '/admin/tags',  grad: 'bg-[linear-gradient(145deg,#fbbf24,#d97706)]' },
        { key: 'cats',    title: t('navigation.categories'),           Icon: IconCategory,  path: '/admin/categories',  grad: 'bg-[linear-gradient(145deg,#94a3b8,#475569)]' },
    ];

    const getLanguageBadgeColor = (language) => {
        switch (language) {
            case 'bangla':  return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
            case 'english': return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'mixed':   return 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
            default:        return 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400';
        }
    };

    return (
        <div className="w-full font-hind space-y-5 sm:space-y-6 py-2 sm:py-4">

            {/* ── Header ── */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                        {t('dashboard.welcomeAdmin', { name: user?.name || 'Admin' })}
                    </h1>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{t('dashboard.adminSubtitle')}</p>
                </div>
                <div className="flex items-center gap-2.5 flex-shrink-0">
                    <button
                        onClick={() => navigate('/admin/settings')}
                        title={t('dashboard.rechargeCredit')}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-50 dark:bg-primary/10 border border-primary-100 dark:border-primary/30 text-primary text-[13px] font-semibold hover:bg-primary-100 dark:hover:bg-primary/20 transition-colors"
                    >
                        <IconCoin size={16} stroke={1.8} />
                        {fmtNum(creditRemaining)} {t('dashboard.creditsUnit')}
                    </button>
                    <span className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-zinc-400 dark:text-zinc-500">
                        <IconCalendar size={15} /> {dateLabel}
                    </span>
                    <button
                        onClick={() => navigate('/admin/template-sets')}
                        className="flex items-center gap-1.5 px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 dark:bg-zinc-100 dark:hover:bg-white text-white dark:text-zinc-900 text-[13px] font-semibold rounded-lg transition-colors"
                    >
                        <IconTemplate size={16} stroke={2} /> {t('dashboard.newTemplate')}
                    </button>
                </div>
            </div>

            {/* ── Content summary (full width): all admin content + credit stats ── */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{t('dashboard.contentSummary')}</span>
                        <span className="text-[11px] text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 px-2 py-0.5 rounded-full">{t('dashboard.live')}</span>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-3">
                    {statTiles.map(({ key, type, label, value, Icon, icon, grad, path, url, tile, accent, sub, hint }) => {
                        // Reserved empty cell — occupies the slot but renders nothing.
                        if (type === 'blank') return <div key={key} aria-hidden className="hidden lg:block" />;
                        return (
                            <button
                                key={key}
                                onClick={() => type === 'external' ? window.open(url, '_blank', 'noopener,noreferrer') : navigate(path)}
                                className={`text-left p-3.5 border rounded-xl transition-shadow hover:shadow-sm ${tile}`}
                            >
                                <div className="flex items-center justify-between mb-2.5 gap-2">
                                    <span className={`text-[13px] font-medium ${accent}`}>{label}</span>
                                    <ModuleSquircleIcon src={icon} Icon={Icon} gradient={grad} size={44} className="drop-shadow-md" />
                                </div>
                                {type === 'action' ? (
                                    <div className={`text-[13px] font-semibold mt-3 flex items-center gap-1 ${accent}`}>
                                        {t('dashboard.manageDesc')} <IconChevronRight size={14} stroke={2} />
                                    </div>
                                ) : type === 'external' ? (
                                    <div className={`text-[13px] font-semibold mt-3 flex items-center gap-1 ${accent}`}>
                                        {hint} <IconExternalLink size={13} stroke={2} />
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">{fmtNum(value)}</div>
                                        <div className={`text-xs font-semibold mt-1.5 ${accent}`}>{type === 'stat' ? sub : t('dashboard.totalLabel')}</div>
                                    </>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Manage / quick actions ── */}
            <div>
                <div className="flex items-center justify-between mb-3.5">
                    <span className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{t('dashboard.toolsAndFeatures')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                    {manageCards.map(({ key, title, Icon, icon, grad, path }) => (
                        <button
                            key={key}
                            onClick={() => navigate(path)}
                            className="group bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl p-4 flex items-center gap-3 text-left transition-all hover:border-primary/40 hover:shadow-sm"
                        >
                            <ModuleSquircleIcon src={icon} Icon={Icon} gradient={grad} size={52} className="drop-shadow-md" />
                            <span className="flex-1 min-w-0">
                                <span className="block text-[14.5px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</span>
                                <span className="block text-[12.5px] text-zinc-500 dark:text-zinc-400 mt-0.5">{t('dashboard.manageDesc')}</span>
                            </span>
                            <IconChevronRight size={18} className="text-zinc-300 dark:text-zinc-600 flex-shrink-0" />
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Draft template sets ── */}
            {draftSets.length > 0 && (
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{t('templateSets.draftTemplateSets') || 'ড্রাফট টেমপ্লেট সেট'}</span>
                        <button onClick={() => navigate('/admin/template-sets')} className="text-[13px] font-semibold text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors">
                            {t('common.viewAll')} →
                        </button>
                    </div>
                    <div>
                        {draftSets.slice(0, 5).map((set, i, arr) => (
                            <div
                                key={set.id}
                                className={`flex items-center gap-3.5 py-3 cursor-pointer ${i < arr.length - 1 ? 'border-b border-zinc-100 dark:border-zinc-800' : ''}`}
                                onClick={() => navigate('/admin/template-sets')}
                            >
                                <div className="w-[38px] h-[38px] rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex items-center justify-center flex-shrink-0">
                                    <IconFileText size={19} stroke={1.6} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">{set.name || set.title || '—'}</p>
                                    <div className="flex gap-1.5 mt-1.5">
                                        <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded">{t('templateSets.draft') || 'ড্রাফট'}</span>
                                        {set.category?.name && (
                                            <span className="text-[11px] font-semibold text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded">{set.category.name}</span>
                                        )}
                                        {set.language && (
                                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getLanguageBadgeColor(set.language)}`}>{t(`templateSets.${set.language}`)}</span>
                                        )}
                                    </div>
                                </div>
                                <IconChevronRight size={18} className="text-zinc-300 dark:text-zinc-600 flex-shrink-0" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );
}
